// seo-schema/jsonld-blocks.ts
//
// Pure factories returning schema.org JSON-LD objects. Platform-agnostic:
// v4 injects via react-helmet adapter (Phase 2), v5 renders via its existing
// shared/components/seo/JsonLd.tsx (Phase 3 sync).
//
// COMPATIBILITY CONTRACT (spec §1 rule 2): every export that also exists in
// v5's shared/seo/jsonld-blocks.ts keeps its exact signature and, with default
// arguments, its exact v5 output (bus defaults preserved). New capability is
// additive only. The JsonLdBlock type is defined here (not imported from a
// renderer) so this module stays framework-free.
import { ORG } from "./org-data";
import type { OfficeLocation } from "./office-locations";

const CONTEXT = "https://schema.org";

export interface JsonLdBlock {
  "@context"?: string;
  "@type": string | string[];
  [key: string]: unknown;
}

/** Stable entity address for the one canonical Flymya node (before/after doc). */
export function orgId(siteUrl: string): string {
  return `${siteUrl}/#organization`;
}

export interface JsonLdGraph {
  "@context": string;
  "@graph": Array<Omit<JsonLdBlock, "@context">>;
}

/** Wrap blocks into a single @graph payload (one <script> per page). */
export function graphBlock(blocks: JsonLdBlock[]): JsonLdGraph {
  return {
    "@context": CONTEXT,
    "@graph": blocks.map(({ "@context": _context, ...rest }) => rest),
  };
}

// ---------------------------------------------------------------------------
// v5-compatible factories (same names/signatures; organization enriched)
// ---------------------------------------------------------------------------

export function organizationBlock(siteUrl: string): JsonLdBlock {
  const block: JsonLdBlock = {
    "@context": CONTEXT,
    // Both: the corporate entity (Organization) AND the local travel-agency face
    // (TravelAgency ⊂ LocalBusiness). The latter legitimizes paymentAccepted and
    // matches how every page's `provider` already references this @id as a TravelAgency.
    "@type": ["Organization", "TravelAgency"],
    "@id": orgId(siteUrl),
    name: ORG.name,
    url: siteUrl,
    logo: `${siteUrl}${ORG.logoPath}`,
    description: ORG.description,
    foundingDate: ORG.foundingDate,
    telephone: ORG.telephone,
    email: ORG.email,
    address: { "@type": "PostalAddress", ...ORG.address },
    sameAs: [...ORG.sameAs],
    memberOf: ORG.memberOf.map((m) => ({
      "@type": "Organization",
      name: m.name,
      ...(m.identifier ? { identifier: m.identifier } : {}),
      ...(m.url ? { url: m.url } : {}),
    })),
    award: ORG.award,
    numberOfEmployees: { "@type": "QuantitativeValue", value: ORG.numberOfEmployees },
    knowsAbout: [...ORG.knowsAbout],
    // Org-level languages: knowsLanguage (Organization); availableLanguage stays
    // on the ContactPoint nodes below, where schema.org actually defines it.
    knowsLanguage: [...ORG.availableLanguage],
    paymentAccepted: ORG.paymentAccepted,
    contactPoint: contactPointBlocks(),
    employee: ORG.leadership.map((p) => ({
      "@type": "Person",
      name: p.name,
      jobTitle: p.jobTitle,
      sameAs: p.sameAs,
    })),
    parentOrganization: {
      "@type": "Organization",
      name: ORG.parentOrganization.name,
      description: ORG.parentOrganization.description,
      foundingDate: ORG.parentOrganization.foundingDate,
      address: { "@type": "PostalAddress", addressCountry: ORG.parentOrganization.addressCountry },
    },
    subOrganization: {
      "@type": "Organization",
      name: ORG.subOrganization.name,
      description: ORG.subOrganization.description,
      ...(ORG.subOrganization.url ? { url: ORG.subOrganization.url } : {}),
    },
    legalName: ORG.legalName,
  };
  return block;
}

export function travelAgencyBlock(siteUrl: string): JsonLdBlock {
  return {
    "@context": CONTEXT,
    "@type": "TravelAgency",
    "@id": orgId(siteUrl),
    name: ORG.name,
    url: siteUrl,
    image: `${siteUrl}${ORG.logoPath}`,
    telephone: ORG.telephone,
    address: { "@type": "PostalAddress", ...ORG.address },
    areaServed: "MM",
    priceRange: ORG.priceRange,
  };
}

export function webSiteBlock(
  siteUrl: string,
  locale: string,
  // undefined → v5-compatible bus default; null → no SearchAction (v4 pages).
  searchAction?: { target: string; queryInput: string } | null,
): JsonLdBlock {
  const block: JsonLdBlock = {
    "@context": CONTEXT,
    "@type": "WebSite",
    name: ORG.name,
    url: `${siteUrl}/${locale}`,
  };
  if (searchAction !== null) {
    const action = searchAction ?? {
      target: `${siteUrl}/${locale}/busv2?q={search_term_string}`,
      queryInput: "required name=search_term_string",
    };
    block.potentialAction = {
      "@type": "SearchAction",
      target: action.target,
      "query-input": action.queryInput,
    };
  }
  return block;
}

export function serviceBlock(opts: {
  siteUrl: string;
  locale: string;
  name: string;
  description: string;
  // Additive (spec §2): default preserves v5's bus output exactly.
  serviceType?: string;
  path?: string;
}): JsonLdBlock {
  const path = opts.path ?? "busv2";
  return {
    "@context": CONTEXT,
    "@type": "Service",
    serviceType: opts.serviceType ?? "Bus ticket booking",
    name: opts.name,
    description: opts.description,
    provider: { "@type": "TravelAgency", "@id": orgId(opts.siteUrl), name: ORG.name, url: opts.siteUrl },
    areaServed: { "@type": "Country", name: "Myanmar" },
    url: `${opts.siteUrl}/${opts.locale}/${path.replace(/^\//, "")}`,
  };
}

export function faqBlock(items: Array<{ q: string; a: string }>): JsonLdBlock {
  return {
    "@context": CONTEXT,
    "@type": "FAQPage",
    mainEntity: items.map((it) => ({
      "@type": "Question",
      name: it.q,
      acceptedAnswer: { "@type": "Answer", text: it.a },
    })),
  };
}

export function breadcrumbBlock(items: Array<{ name: string; item: string }>): JsonLdBlock {
  return {
    "@context": CONTEXT,
    "@type": "BreadcrumbList",
    itemListElement: items.map((it, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: it.name,
      item: it.item,
    })),
  };
}

export function productBlock(opts: {
  name: string;
  description: string;
  price: number;
  currency: string;
  url: string;
  availability?: "InStock" | "OutOfStock";
}): JsonLdBlock {
  return {
    "@context": CONTEXT,
    "@type": "Product",
    name: opts.name,
    description: opts.description,
    offers: {
      "@type": "Offer",
      price: opts.price.toFixed(2),
      priceCurrency: opts.currency,
      availability: `https://schema.org/${opts.availability ?? "InStock"}`,
      url: opts.url,
    },
  };
}

export function itemListBlock(products: JsonLdBlock[]): JsonLdBlock {
  return {
    "@context": CONTEXT,
    "@type": "ItemList",
    itemListElement: products.map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: p,
    })),
  };
}

// ---------------------------------------------------------------------------
// New factories (spec §2)
// ---------------------------------------------------------------------------

export function contactPointBlocks(): JsonLdBlock[] {
  return [
    {
      "@type": "ContactPoint",
      contactType: "customer service",
      telephone: ORG.telephone,
      email: ORG.email,
      availableLanguage: [...ORG.availableLanguage],
      hoursAvailable: "Mo-Su 00:00-24:00",
    },
    { "@type": "ContactPoint", contactType: "customer service", url: ORG.messaging.whatsapp, name: "WhatsApp" },
    { "@type": "ContactPoint", contactType: "customer service", url: ORG.messaging.viber, name: "Viber" },
    { "@type": "ContactPoint", contactType: "customer service", url: ORG.messaging.messenger, name: "Facebook Messenger" },
  ];
}

/** One physical office as a LocalBusiness node linked to the org (§2 new). */
export function localBusinessBlock(office: OfficeLocation, siteUrl: string): JsonLdBlock {
  return {
    "@context": CONTEXT,
    "@type": "TravelAgency",
    "@id": `${siteUrl}/#${office.id}`,
    name: office.name,
    parentOrganization: { "@id": orgId(siteUrl) },
    address: {
      "@type": "PostalAddress",
      streetAddress: `${office.streetAddress}, ${office.township}`,
      addressLocality: office.addressLocality,
      ...(office.postalCode ? { postalCode: office.postalCode } : {}),
      addressCountry: office.addressCountry,
    },
    telephone: office.telephone,
    openingHours: office.openingHours,
    hasMap: office.hasMap,
    image: `${siteUrl}${ORG.logoPath}`,
    priceRange: ORG.priceRange,
  };
}
